import { useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import Table from "../../../components/Table/Table";
import Badge from "../../../components/Badge";
import Modal from "../../../components/Modal";
import InputGroup from "../../../components/FormComponent/InputGroup";
import Button from "../../../components/Button";
import { httprequest } from "../../../data/api";

const ReferralOverview = ({
  dashboardData,
  isLoading,
  referrers,
  isReferrersLoading,
  setCurrentView,
  setActivePill
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referForm, setReferForm] = useState({
    schoolName: "",
    schoolEmail: "",
    location: ""
  });

  const referrerColumns = [
    { name: "Name", selector: row => row.school_name || row.name || `${row.first_name || ""} ${row.last_name || ""}`, sortable: true },
    { name: "Primary Email", selector: row => row.email || row.school_email, sortable: true },
    { name: "Status", selector: row => row.status || "Pending", sortable: true, cell: row => <Badge status={row.status || "Pending"} /> },
    { name: "Date created", selector: row => row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A", sortable: true }
  ];

  const handleReferFormChange = (e) => {
    const { name, value } = e.target;
    setReferForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReferSubmit = async () => {
    if (!referForm.schoolName || !referForm.schoolEmail) {
      toast.error("Please fill in all required fields");

      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        school_name: referForm.schoolName,
        school_email: referForm.schoolEmail,
        location: referForm.location
      };
      const response = await httprequest.post("/api/v1/referrals/referrer/invite-school", payload);
      toast.success(response?.data?.message || "Referral sent successfully!");
      setIsModalOpen(false);
      setReferForm({ schoolName: "", schoolEmail: "", location: "" });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send referral");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="ref-dash-main">
      <div className="ref-dash-header">
        <div className="ref-dash-title">
          <h1>Dashboard</h1>
          <p>An Over view of your activity</p>
        </div>
        <div className="ref-dash-actions">
          <button className="ref-btn-outline" onClick={() => setCurrentView("bank")}>My Bank</button>
          <button className="ref-btn-primary" onClick={() => setIsModalOpen(true)}>Refer a School</button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="ref-dash-stats">
        <div className="ref-stat-card">
          <div className="ref-stat-info">
            <span className="ref-stat-label">Total Referrals</span>
            <span className="ref-stat-value">{isLoading ? "..." : dashboardData?.total_referrals || 0}</span>
          </div>
          <div className="ref-stat-icon"><Icon icon="fluent:people-community-20-regular" /></div>
        </div>
        <div className="ref-stat-card">
          <div className="ref-stat-info">
            <span className="ref-stat-label">Successful Referrals</span>
            <span className="ref-stat-value">{isLoading ? "..." : dashboardData?.successful_referrals || 0}</span>
          </div>
          <div className="ref-stat-icon"><Icon icon="fluent:checkmark-circle-20-regular" /></div>
        </div>
        <div className="ref-stat-card">
          <div className="ref-stat-info">
            <span className="ref-stat-label">Points Earned</span>
            <span className="ref-stat-value">{isLoading ? "..." : dashboardData?.points_earned || 0}</span>
          </div>
          <div className="ref-stat-icon"><Icon icon="ph:coins" /></div>
        </div>
        <div className="ref-stat-card">
          <div className="ref-stat-info">
            <span className="ref-stat-label">Pending Rewards</span>
            <span className="ref-stat-value">{isLoading ? "..." : dashboardData?.pending_points || 0}</span>
          </div>
          <div className="ref-stat-icon"><Icon icon="fluent:clock-20-regular" /></div>
        </div>
      </div>

      {/* WhatsApp Community Banner */}
      <div className="whatsapp-community-banner">
        <div className="whatsapp-banner-content">
          <h2>Join Our WhatsApp Community</h2>
          <p>Connect with other parents teachers, and school administrators for important updates.</p>
          <a href="https://chat.whatsapp.com/L3D31nqZnAxII6P0OLTT8J?s=cl&p=a&ilr=1" target="_blank" rel="noopener noreferrer" className="whatsapp-join-btn">
            Join Now <Icon icon="logos:whatsapp-icon" width="20" height="20" />
          </a>
        </div>
        <div className="whatsapp-banner-qr">
          <p>Scan to join</p>
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://chat.whatsapp.com/L3D31nqZnAxII6P0OLTT8J?s=cl&p=a&ilr=1" alt="WhatsApp QR Code" />
        </div>
      </div>

      {/* Referral Code Section */}
      <div className="ref-code-section">
        <div className="ref-code-header">
          <h2>Your Referral Code</h2>
          <p>Send the link to Schools you want to refer</p>
        </div>
        <div className="ref-code-box" onClick={() => {
          const codeLink = dashboardData?.referral_link || "";
          if (codeLink) {
            navigator.clipboard.writeText(codeLink);
            toast.success("Copied to clipboard!");
          }
        }}
        >
          <span className="ref-code-text">
            {isLoading ? "Loading..." : dashboardData?.referral_link || "N/A"}
          </span>
          <span className="ref-code-copy">Tap to copy</span>
        </div>
      </div>

      {/* Referrers Table */}
      <div className="ref-referrers-section">
        <div className="ref-table-header">
          <div className="ref-table-title">
            <h2>All Referred Users</h2>
            <p>See all the users you have referred</p>
          </div>
          <span className="ref-table-view-all" style={{ cursor: "pointer" }} onClick={() => {
            setActivePill("Referred User"); setCurrentView("referred");
          }}
          >
            View All
          </span>
        </div>
        <Table data={referrers || []} columns={referrerColumns} pagination isLoading={isReferrersLoading} />
      </div>

      <Modal
        isopen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title="Refer a school"
        subtitle="Refer a school to Shelf21"
        modalContent={
          <div className="ref-modal-form">
            <InputGroup
              label="School Name"
              required={true}
              inputType="text"
              inputName="schoolName"
              value={referForm.schoolName}
              onChange={handleReferFormChange}
              placeHolder="Name"
            />
            <InputGroup
              label="School Email"
              required={true}
              inputType="email"
              inputName="schoolEmail"
              value={referForm.schoolEmail}
              onChange={handleReferFormChange}
              placeHolder=""
            />
            <InputGroup
              label="Location"
              required={false}
              inputType="text"
              inputName="location"
              value={referForm.location}
              onChange={handleReferFormChange}
              placeHolder="Enter location"
            />
          </div>
        }
        modalFooter={
          <div className="ref-modal-footer">
            <Button
              buttonName="Cancel"
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              onClick={() => setIsModalOpen(false)}
            />
            <Button
              buttonName={isSubmitting ? "Sending..." : "Send an invite"}
              variant="primary"
              buttonSize="full"
              color="btndefault"
              onClick={handleReferSubmit}
              disabled={isSubmitting}
            />
          </div>
        }
      />
    </main>
  );
};

export default ReferralOverview;
